import {
	ApplicationConfig,
	LOCALE_ID,
	importProvidersFrom,
	inject,
	provideAppInitializer,
	provideZoneChangeDetection,
} from '@angular/core';
import { registerLocaleData } from '@angular/common';
import localePl from '@angular/common/locales/pl';
import { provideRouter } from '@angular/router';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { provideAnimations } from '@angular/platform-browser/animations';
import { MAT_SNACK_BAR_DEFAULT_OPTIONS } from '@angular/material/snack-bar';
import { MAT_TOOLTIP_DEFAULT_OPTIONS } from '@angular/material/tooltip';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { provideTranslateHttpLoader } from '@ngx-translate/http-loader';
import { NgxMaskModule } from 'ngx-mask';
import { firstValueFrom } from 'rxjs';

import { routes } from './app.routes';
import { authInterceptor } from '@services/auth';
import { rateLimitInterceptor } from './core/interceptors/rate-limit.interceptor';
import { retryInterceptor } from './core/interceptors/retry.interceptor';
import { SocketService } from '@services/socket.service';
import { CookieService } from 'ngx-cookie-service';

// Liczby w całej aplikacji po polsku (spacja tysięcy, przecinek dziesiętny) — w obu językach
// portalu, tak jak ceny (`@helpers/price-format`). Daty ze słownymi miesiącami idą przez
// `appDate`, który bierze język portalu.
registerLocaleData(localePl, 'pl');

export const appConfig: ApplicationConfig = {
	providers: [
		provideZoneChangeDetection({ eventCoalescing: true }),
		{ provide: LOCALE_ID, useValue: 'pl' },
		provideRouter(routes),
		// Disable touch-triggered Material tooltips app-wide: on mobile a tap/long-press
		// to scroll was popping tooltips instead of moving the view (hard to navigate).
		// Desktop hover tooltips are unaffected.
		{ provide: MAT_TOOLTIP_DEFAULT_OPTIONS, useValue: { touchGestures: 'off' } },
		provideHttpClient(
			withInterceptors([authInterceptor, retryInterceptor, rateLimitInterceptor]),
		),
		provideAnimations(),
		// ngx-translate v17 with standalone API
		importProvidersFrom(
			TranslateModule.forRoot({
				fallbackLang: 'en',
			}),
			NgxMaskModule.forRoot(),
		),
		// Provide HTTP loader for translations
		provideTranslateHttpLoader({
			prefix: './assets/i18n/',
			suffix: '.json',
		}),
		// Initialize translations before app starts
		provideAppInitializer(() => {
			const translate = inject(TranslateService);
			translate.addLangs(['en', 'pl']);
			// Polish-first: start in PL before the stored preference is applied (avoids an EN flash
			// + an EN Accept-Language on early API calls). TranslationService later applies the
			// user's stored choice if they picked another language.
			return firstValueFrom(translate.use('pl'));
		}),
		SocketService,
		CookieService,
		{ provide: MAT_SNACK_BAR_DEFAULT_OPTIONS, useValue: { duration: 5000 } },
	],
};
